import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Share2, Facebook, Copy, Check, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import analytics from "@/lib/segment";

// Reddit icon component
const RedditIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

interface SharePlaybookDialogProps {
  playbookTitle: string;
  playbookSlug: string;
  playbookDescription?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
}

export function SharePlaybookDialog({ 
  playbookTitle, 
  playbookSlug, 
  playbookDescription,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  trigger
}: SharePlaybookDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = controlledOnOpenChange || setInternalOpen;

  const generateShareUrl = async () => {
    if (shareUrl || !playbookSlug) return; // Already generated or no slug
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-share-link", {
        body: { 
          slug: playbookSlug,
          title: playbookTitle
        }
      });

      if (error) throw error;
      
      setShareUrl(data.shortUrl || data.url);
    } catch (error) {
      console.error("Error creating share link:", error);
      // Fallback to direct URL if Dub.co fails
      const fallbackUrl = `${window.location.origin}/share/${playbookSlug}`;
      setShareUrl(fallbackUrl);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset state when dialog closes or slug changes
  useEffect(() => {
    if (!isOpen) {
      setShareUrl("");
      setIsLoading(false);
      setCopied(false);
    }
  }, [isOpen]);

  // Generate URL when dialog opens
  useEffect(() => {
    if (isOpen && playbookSlug && !shareUrl) {
      generateShareUrl();
    }
  }, [isOpen, playbookSlug]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      analytics.track("Playbook shared", {
        playbook_name: playbookTitle,
        share_method: "link",
      });
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const shareToFacebook = () => {
    analytics.track("Playbook shared", {
      playbook_name: playbookTitle,
      share_method: "facebook",
    });
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(`Check out this Turo host playbook: ${playbookTitle}`)}`;
    window.open(facebookUrl, "_blank", "width=600,height=400");
  };

  const shareToReddit = () => {
    analytics.track("Playbook shared", {
      playbook_name: playbookTitle,
      share_method: "reddit",
    });
    const redditUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(`${playbookTitle} - Turo Host Playbook`)}`;
    window.open(redditUrl, "_blank", "width=600,height=600");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {trigger !== undefined ? (
        trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share "{playbookTitle}"</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          {/* Share URL */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Share Link</label>
            <div className="flex gap-2">
              {isLoading ? (
                <div className="flex-1 flex items-center justify-center h-10 bg-muted rounded-md">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Input 
                  value={shareUrl} 
                  readOnly 
                  className="flex-1 text-foreground bg-background"
                />
              )}
              <Button 
                variant="outline" 
                size="icon"
                onClick={copyToClipboard}
                disabled={isLoading || !shareUrl}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Social Share Buttons */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Share to</label>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={shareToFacebook}
                disabled={isLoading || !shareUrl}
              >
                <Facebook className="h-4 w-4 mr-2 text-[#1877F2]" />
                Facebook
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={shareToReddit}
                disabled={isLoading || !shareUrl}
              >
                <RedditIcon className="h-4 w-4 mr-2 text-[#FF4500]" />
                Reddit
              </Button>
            </div>
          </div>

          {/* Preview link */}
          {shareUrl && (
            <div className="pt-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-muted-foreground"
                onClick={() => window.open(shareUrl, "_blank")}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Preview shared page
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
